import { Users, TrendingUp, Award, Clock, Download } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { MetricCard } from '@/components/molecules/MetricCard';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Avatar } from '@/components/atoms/Avatar';
import { Separator } from '@/components/atoms/Separator';
import { Spinner } from '@/components/atoms/Spinner';
import { useCompanyMetrics, useExportReport } from '@/hooks/useCompanyData';
import { formatTime } from '@/utils/formatTime';
import styles from './DashboardPage.module.scss';
import type { ExportReportRequest } from '@/types/company.types';

export function DashboardPage() {
  const { metrics, isLoading } = useCompanyMetrics();
  const { exportReportData, isExporting } = useExportReport();

  const topPerformers = metrics?.topPerformers ?? [];
  const recentActivity = metrics?.recentActivity ?? [];

  const handleExportReport = async () => {
    const request: ExportReportRequest = {
      reportType: 'employees',
      format: 'csv',
    };

    const result = await exportReportData(request);
    if (result) {
      window.open(result.downloadUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.dashboard__loading}>
          <Spinner size="lg" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className={styles.dashboard}>
        <p>Failed to load dashboard metrics.</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboard__header}>
        <h1 className={styles.dashboard__title}>Company Dashboard</h1>
        <Button
          variant="outline"
          leftIcon={<Download />}
          onClick={handleExportReport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </div>

      <div className={styles.dashboard__metrics}>
        <MetricCard
          icon={Users}
          label="Total Employees"
          value={metrics.totalEmployees.toString()}
          description={`${metrics.activeEmployees} active`}
        />
        <MetricCard
          icon={Award}
          label="Stories Completed"
          value={metrics.totalStoriesCompleted.toString()}
          description={`${metrics.averageCompletionRate}% completion rate`}
        />
        <MetricCard
          icon={TrendingUp}
          label="Total XP Earned"
          value={metrics.totalXPEarned.toLocaleString()}
          description={`Avg level: ${metrics.averageLevel}`}
        />
        <MetricCard
          icon={Clock}
          label="Time Spent Learning"
          value={formatTime(metrics.averageTimeSpent)}
          description="Average per employee"
        />
      </div>

      <div className={styles.dashboard__content}>
        <Card className={styles.dashboard__topPerformers}>
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.dashboard__performersList}>
              {topPerformers.map((employee, index) => (
                <div key={employee.id} className={styles.dashboard__performerItem}>
                  <div className={styles.dashboard__performerRank}>#{index + 1}</div>
                  <Avatar
                    src={employee.avatar}
                    alt={employee.name}
                    fallback={employee.name}
                    size="md"
                  />
                  <div className={styles.dashboard__performerInfo}>
                    <span className={styles.dashboard__performerName}>{employee.name}</span>
                    <span className={styles.dashboard__performerEmail}>{employee.email}</span>
                  </div>
                  <div className={styles.dashboard__performerStats}>
                    <Badge variant="default">{employee.level}</Badge>
                    <span className={styles.dashboard__performerXP}>
                      {employee.stats.totalXP.toLocaleString()} XP
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={styles.dashboard__recentActivity}>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.dashboard__activityList}>
              {recentActivity.map((activity, index) => (
                <div key={activity.id}>
                  {index > 0 && <Separator className={styles.dashboard__activitySeparator} />}
                  <div className={styles.dashboard__activityItem}>
                    <div className={styles.dashboard__activityInfo}>
                      <span className={styles.dashboard__activityName}>
                        {activity.employeeName}
                      </span>
                      <span className={styles.dashboard__activityDescription}>
                        {activity.description}
                      </span>
                    </div>
                    <span className={styles.dashboard__activityTime}>
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DashboardPage;
